import constants from '../../../constants';
import { AccountActivityRequest, AccountActivityResponse } from './types';

const API_BASE = constants.baseApiHost;
const API_KEY = constants.apikey;

// Session helpers (reuse pattern from backofficeApi)
const getSession = () => {
  const localStorageSession = localStorage['session'];
  return localStorageSession && localStorageSession !== 'undefined'
    ? JSON.parse(localStorageSession)
    : null;
};

const getHeaders = (): Record<string, string> => {
  const session = getSession();
  return {
    'ApiKey': API_KEY || '',
    'Content-Type': 'application/json',
    'Authorization': session?.key ? `bearer ${session.key}` : '',
  };
};

const handleResponse = async <T>(res: Response): Promise<T> => {
  if (!res.ok) {
    throw new Error(`API ${res.status} ${res.statusText}`);
  }
  return res.json();
};

/**
 * Fetch account activity records for audit trail
 * 
 * @param params - Query parameters (only one filter plus page is allowed)
 * @returns Paginated list of activity records
 */
export const fetchAccountActivity = async (
  params: AccountActivityRequest
): Promise<AccountActivityResponse> => {
  const queryParams = new URLSearchParams();

  if (params.organizationId !== undefined) {
    queryParams.append('organizationId', String(params.organizationId));
  }
  if (params.targetOrganizationId !== undefined) {
    queryParams.append('targetOrganizationId', String(params.targetOrganizationId));
  }
  if (params.targetCampaignId !== undefined) {
    queryParams.append('targetCampaignId', String(params.targetCampaignId));
  }
  if (params.targetEventId !== undefined) {
    queryParams.append('targetEventId', String(params.targetEventId));
  }
  if (params.targetEventAmbassadorId !== undefined) {
    queryParams.append('targetEventAmbassadorId', String(params.targetEventAmbassadorId));
  }
  if (params.targetFundraiserId !== undefined) {
    queryParams.append('targetFundraiserId', String(params.targetFundraiserId));
  }
  if (params.targetTeamId !== undefined) {
    queryParams.append('targetTeamId', String(params.targetTeamId));
  }
  if (params.targetCollectionId !== undefined) {
    queryParams.append('targetCollectionId', String(params.targetCollectionId));
  }
  if (params.targetSaleId !== undefined) {
    queryParams.append('targetSaleId', String(params.targetSaleId));
  }
  if (params.targetDonationId !== undefined) {
    queryParams.append('targetDonationId', String(params.targetDonationId));
  }
  if (params.accountId !== undefined) {
    queryParams.append('accountId', String(params.accountId));
  }
  if (params.page !== undefined) {
    queryParams.append('page', String(params.page));
  }
  if (params.changesAndExportsOnly) {
    queryParams.append('changesAndExportsOnly', 'true');
  }

  const queryString = queryParams.toString();
  const url = `${API_BASE}/api/v2/account/activity${queryString ? `?${queryString}` : ''}`;

  const res = await fetch(url, { headers: getHeaders() });
  return handleResponse<AccountActivityResponse>(res);
};

/**
 * Fetch all audit trail records (no filters)
 * 
 * @param changesAndExportsOnly - Optional filter for changes and exports only
 * @returns Paginated list of all activity records
 */
export const fetchAllAuditTrail = async (changesAndExportsOnly?: boolean, page?: number): Promise<AccountActivityResponse> => {
  const queryParams = new URLSearchParams();
  
  if (changesAndExportsOnly) {
    queryParams.append('changesAndExportsOnly', 'true');
  }
  if (page !== undefined) {
    queryParams.append('page', String(page));
  }

  const queryString = queryParams.toString();
  const url = `${API_BASE}/api/v2/account/audit/trail${queryString ? `?${queryString}` : ''}`;

  const res = await fetch(url, { headers: getHeaders() });
  return handleResponse<AccountActivityResponse>(res);
};
